import React, { useState, useRef, useEffect } from "react";
import styles from "../../FilePreviewModal.module.scss";
import { EThemeType } from "redux/reducers";
import classNames from "classnames";

interface CodePreviewProps {
  content: string;
  language: string;
  theme?: EThemeType;
}

const formatCode = (content: string, language: string): string => {
  try {
    // JSON formatting
    if (language === "json") {
      const parsed = JSON.parse(content);
      return JSON.stringify(parsed, null, 2);
    }
    // HTML/XML formatting
    else if (language === "htm" || language === "xml") {
      return formatHTML(content);
    }
    // Default - return original content
    return content;
  } catch (e) {
    console.error("Error formatting code:", e);
    return content;
  }
};
// Completely fixed HTML formatter with no unused variables
const formatHTML = (content: string): string => {
  let formatted = '';
  let indent = 0;
  let inComment = false;
  
  // First, normalize line breaks and spacing
  content = content.replace(/\r\n/g, '\n');
  content = content.replace(/\n\s+/g, '\n');
  
  // Elements that should always have line breaks before and after
  const blockElements: string[] = [
    'div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'table', 'tr', 'form', 'header',
    'footer', 'section', 'article', 'nav', 'aside',
    'main', 'figure', 'figcaption', 'blockquote'
  ];
  
  // Head section elements that should have line breaks between them
  const headElements: string[] = [
    'title', 'meta', 'link', 'script', 'style', 'base'
  ];
  
  // Helper to check if a tag is a specific element type
  const isElementType = (tag: string, elementList: string[]): boolean => {
    return elementList.some((el: string) => 
      tag.toLowerCase().startsWith(el + '>') || 
      tag.toLowerCase().startsWith(el + ' ')
    );
  };
  
  // Process the HTML content character by character
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    
    // Handle comments
    if (char === '<' && content.substring(i, i + 4) === '<!--') {
      inComment = true;
      formatted += '\n' + ' '.repeat(indent) + '<!--';
      i += 3;
      continue;
    }
    
    if (inComment && content.substring(i, i + 3) === '-->') {
      inComment = false;
      formatted += '-->\n';
      i += 2;
      continue;
    }
    
    if (inComment) {
      formatted += char;
      continue;
    }
    
    // Handle opening tags
    if (char === '<' && content[i+1] !== '/' && content[i+1] !== '!' && content[i+1] !== '?') {
      // We'll extract tag info when we process the ">" character
      
      // Add line break before tag
      if (i > 0) {
        formatted += '\n' + ' '.repeat(indent);
      }
      
      // Add the opening part of the tag
      formatted += char;
      
      continue;
    }
    
    // Handle closing tags
    if (char === '<' && content[i+1] === '/') {
      // Extract the tag name
      let tagEnd = content.indexOf('>', i);
      if (tagEnd === -1) tagEnd = content.length; // Safety check
      
      let tagName = content.substring(i + 2, tagEnd).trim();
      
      // Decrease indentation for block elements and head elements
      if (isElementType(tagName, blockElements) || isElementType(tagName, headElements)) {
        indent = Math.max(0, indent - 2);
      }
      
      // Add line break before closing tag (except for inline elements)
      if (!isElementType(tagName, ['span', 'a', 'strong', 'em', 'b', 'i', 'u', 'small', 'code'])) {
        formatted += '\n' + ' '.repeat(indent);
      }
      
      // Add the tag
      formatted += '</' + tagName + '>';
      
      // Add extra line break after block elements and head elements
      if (isElementType(tagName, blockElements) || isElementType(tagName, headElements)) {
        formatted += '\n';
      }
      
      // Skip to the end of the closing tag
      i = tagEnd;
      continue;
    }
    
    // Handle DOCTYPE and processing instructions
    if (char === '<' && (content[i+1] === '!' || content[i+1] === '?')) {
      let endPos = content.indexOf('>', i);
      if (endPos === -1) endPos = content.length; // Safety check
      
      if (i > 0) {
        formatted += '\n';
      }
      
      // Add the declaration
      formatted += content.substring(i, endPos + 1);
      formatted += '\n';
      
      // Skip to the end
      i = endPos;
      continue;
    }
    
    // Handle the end of an opening tag
    if (char === '>') {
      formatted += char;
      
      // Extract the tag we just closed
      let tagStart = formatted.lastIndexOf('<');
      let tag = formatted.substring(tagStart, formatted.length);
      
      // Get the tag name
      let spacePos = tag.indexOf(' ');
      let tagName = spacePos !== -1 
        ? tag.substring(1, spacePos) 
        : tag.substring(1, tag.length - 1);
      
      // Check if it's a self-closing tag
      const isSelfClosing = tag.endsWith('/>') || 
                          ['img', 'br', 'hr', 'input', 'meta', 'link'].includes(tagName.toLowerCase());
      
      // Add indentation for non-self-closing block elements and head elements
      if (!isSelfClosing && (isElementType(tagName, blockElements) || isElementType(tagName, headElements))) {
        indent += 2;
        
        // If the next character is not the start of a closing tag, add a line break
        if (i + 1 < content.length && content[i+1] !== '<') {
          formatted += '\n' + ' '.repeat(indent);
        }
      }
      
      // For head elements like title, meta, etc., always add a line break
      if (isElementType(tagName, headElements)) {
        formatted += '\n';
      }
      
      continue;
    }
    
    // Regular content
    formatted += char;
  }
  
  // Clean up any excess line breaks
  formatted = formatted.replace(/\n\s*\n\s*\n/g, '\n\n');
  
  return formatted;
};

const CodePreview: React.FC<CodePreviewProps> = ({ content, language, theme }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isFormatted, setIsFormatted] = useState<boolean>(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState<boolean>(false);
  const [displayedContent, setDisplayedContent] = useState<string>(content);
  const [canFormat, setCanFormat] = useState<boolean>(false);
  const codeRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  // Check if the current language supports formatting
  useEffect(() => {
    const formattableLanguages = ['json','htm', 'xml', 'txt', 'text'];
    setCanFormat(formattableLanguages.includes(language.toLowerCase()));
    setDisplayedContent(content);
    setIsFormatted(false); // Reset format state when content changes
  }, [content, language]);
  
  // Format/unformat code
  const handleFormatToggle = () => {
    try {
      if (!isFormatted) {
        // Apply formatting
        const formatted = formatCode(content, language);
        setDisplayedContent(formatted);
      } else {
        // Restore original content
        setDisplayedContent(content);
      }
      setIsFormatted(!isFormatted);
    } catch (error) {
      console.error("Error toggling format:", error);
      setDisplayedContent(content);
      setIsFormatted(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(displayedContent).then(() => {
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }).catch(err => {
      console.error("Failed to copy text: ", err);
    });
  };

  // Ensure scrolling works in collapsed mode
  useEffect(() => {
    if (containerRef.current) {
      // Make sure container has proper overflow
      containerRef.current.style.overflow = 'auto';
      
      // Ensure the code doesn't wrap and scrollbars appear when needed
      if (codeRef.current && codeRef.current.parentElement) {
        codeRef.current.parentElement.style.whiteSpace = 'pre';
        codeRef.current.parentElement.style.overflow = 'visible';
        codeRef.current.style.display = 'inline-block';
        codeRef.current.style.minWidth = 'fit-content';
      }
    }
  }, []);

  const handleExpandCollapse = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    
    if (wrapperRef.current) {
      // Get the parent element up to the fullscreen modal container
      const modalContainer = wrapperRef.current.closest(`.${styles.fullscreenModalContainer}`) as HTMLElement;
      
      if (newExpandedState) {
        // Apply expanded class to wrapper
        wrapperRef.current.style.transition = 'all 0.3s ease';
        wrapperRef.current.classList.add(styles.expandedWrapper);
        
        // Add these style properties directly
        wrapperRef.current.style.display = 'flex';
        wrapperRef.current.style.justifyContent = 'center';
        wrapperRef.current.style.alignItems = 'center';
        
        // Get reference to the container and reset any positioning that might interfere
        const container = wrapperRef.current.querySelector(`.${styles.codePreviewContainer}`) as HTMLElement;
        if (container) {
          container.style.width = '100%';
          container.style.height = '100%';
          container.style.maxWidth = 'inherit';
          container.style.margin = '0';
          container.style.position = 'relative';
          container.style.left = '0';
          container.style.top = '0';
          container.style.transform = 'none';
        }
        
        // Make sure code container scrolls in expanded mode
        if (containerRef.current) {
          containerRef.current.style.height = 'auto';
          containerRef.current.style.maxHeight = 'none';
          containerRef.current.style.overflow = 'auto';
        }
        
        // Ensure our component appears above everything
        if (modalContainer) {
          modalContainer.classList.add(styles.expanded);
          modalContainer.style.zIndex = '10000'; 
        }
        
        // Prevent body scrolling
        document.body.style.overflow = 'hidden';
      } else {
        // Restore normal state
        wrapperRef.current.classList.remove(styles.expandedWrapper);
        
        // Remove the inline styles
        wrapperRef.current.style.display = '';
        wrapperRef.current.style.justifyContent = '';
        wrapperRef.current.style.alignItems = '';
        
        // Reset container styles
        const container = wrapperRef.current.querySelector(`.${styles.codePreviewContainer}`) as HTMLElement;
        if (container) {
          container.style.width = '';
          container.style.height = '';
          container.style.maxWidth = '';
          container.style.margin = '';
          container.style.position = '';
          container.style.left = '';
          container.style.top = '';
          container.style.transform = '';
        }
        
        // Reset code container for collapsed mode, but keep scrolling
        if (containerRef.current) {
          containerRef.current.style.height = '300px';
          containerRef.current.style.overflow = 'auto';
        }
        
        // Reset z-index
        if (modalContainer) {
          modalContainer.classList.remove(styles.expanded);
          modalContainer.style.zIndex = '';
        }
        
        // Restore body scrolling
        document.body.style.overflow = '';
      }
    }
  };
    
  // Handle escape key to collapse
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        handleExpandCollapse();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Also ensure body overflow is restored on unmount
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  // Update code display whenever displayedContent changes
  useEffect(() => {
    if (codeRef.current) {
      // Update the text content directly
      codeRef.current.textContent = displayedContent;
    }
  }, [displayedContent, language]);

  return (
    <div 
      ref={wrapperRef} 
      className={classNames(styles.codePreviewWrapper, {
        [styles.expandedWrapper]: isExpanded
      })}
    >
      <div className={styles.codePreviewContainer}>
        <div className={styles.codeToolbar}>
          <div className={styles.toolbarLeft}>
            <button 
              className={classNames(styles.toolbarButton, { [styles.active]: isExpanded })}
              onClick={handleExpandCollapse}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? "Collapse" : "Expand"}
            </button>
            
            <button 
              className={classNames(styles.toolbarButton, { [styles.active]: isFormatted })}
              onClick={handleFormatToggle}
              title={isFormatted ? "Unformat" : "Format"}
              disabled={!canFormat}
            >
              {isFormatted ? "Unformat" : "Format"}
            </button>
            
            <button 
              className={classNames(styles.toolbarButton, { [styles.success]: copiedToClipboard })}
              onClick={handleCopy}
              title="Copy to clipboard"
            >
              {copiedToClipboard ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
        <div 
          ref={containerRef}
          className={classNames(styles.codeContainer, {
            [styles.light]: theme === "light",
            [styles.dark]: theme === "dark"
          })}
          style={{ 
            overflow: 'auto',
            height: isExpanded ? 'auto' : '445px',
            maxHeight: isExpanded ? '100%' : '445px' 
          }}
        >
          <pre className={styles.codeContent} style={{ overflow: 'visible', whiteSpace: 'pre' }}>
            <code ref={codeRef} style={{ display: 'inline-block', minWidth: 'fit-content' }}></code>
          </pre>
        </div>
      </div>
    </div>
  );
};

export default CodePreview;